// DealCard — tarjeta del tablero. Arrastrable en escritorio (HTML5 nativo, sin
// dependencias nuevas); «Mover a…» para teclado y pantallas táctiles.

import { CalendarClock } from 'lucide-react';

import { fmtDay, fmtMoney } from '../model';
import type { Deal, DealStage, FollowUpRow } from '../types';
import { FollowUpChip, MoveMenu, OwnerAvatar } from './parts';

interface DealCardProps {
  deal: Deal;
  stages: DealStage[];
  followUp?: FollowUpRow;
  active?: boolean;
  draggable?: boolean;
  mine?: boolean;
  onOpen: (deal: Deal) => void;
  onMove: (deal: Deal, stage: DealStage) => void;
  onDragStart?: (deal: Deal) => void;
  onDragEnd?: () => void;
}

export function DealCard({ deal, stages, followUp, active, draggable, mine, onOpen, onMove, onDragStart, onDragEnd }: DealCardProps) {
  const client = followUp?.client_name || '';
  return (
    <li
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/deal-id', deal.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart?.(deal);
      }}
      onDragEnd={onDragEnd}
      data-testid={`deal-card-${deal.id}`}
      className={`dl-in group rounded-xl border bg-card p-3 shadow-sm transition-shadow hover:shadow-md ${draggable ? 'cursor-grab active:cursor-grabbing' : ''} ${active ? 'border-primary ring-1 ring-primary/40' : 'border-border'}`}
    >
      <div className="flex items-start gap-2">
        <button type="button" onClick={() => onOpen(deal)} className="min-w-0 flex-1 rounded-lg text-left">
          <span className="line-clamp-2 block text-[13.5px] font-semibold leading-snug text-foreground">{deal.title}</span>
          <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">
            {client || (deal.lead_id ? 'Lead ligado' : 'Sin cliente ligado')}
          </span>
        </button>
        <MoveMenu stages={stages} current={deal.stage_id} onMove={(s) => onMove(deal, s)} compact />
      </div>
      <div className="mt-2.5 flex items-baseline gap-2">
        <span className="text-[15px] font-semibold tabular-nums text-foreground">{fmtMoney(deal.value_amount, deal.currency)}</span>
        <span className="text-[11.5px] tabular-nums text-muted-foreground" title="Probabilidad de cierre">{deal.probability}%</span>
        <span className="ml-auto"><OwnerAvatar ownerId={deal.owner_user_id} mine={mine} /></span>
      </div>
      {(followUp || deal.expected_close_date) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11.5px] text-muted-foreground">
          {followUp && <FollowUpChip row={followUp} />}
          {deal.expected_close_date && (
            <span className="inline-flex items-center gap-1" title="Cierre esperado">
              <CalendarClock className="h-3.5 w-3.5" aria-hidden />Cierre {fmtDay(deal.expected_close_date)}
            </span>
          )}
        </div>
      )}
    </li>
  );
}
